import { useMemo, useState } from "react";
import { Pencil } from "lucide-react";
import { LABELS } from "@match/shared";
import { PHASE_LABEL } from "@/components/session";
import { DataTable, type DataTableColumn } from "@/components/table";
import { Button, Dash, ErrorNote, Field, inputClass } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { date, dateTime, money, signClass } from "@/lib/format";
import { useCyclePeriods, useRefreshDirectory } from "@/lib/queries";
import { decimalInput } from "@/lib/units";
import type { CashMove, CyclePeriodRow, CyclePeriods, CashScanOutcome } from "@/lib/types";

/*
 * I PERIODI DEL CICLO: la sua contabilità, tratto per tratto.
 *
 * Un periodo è una differenza di SALDI del conto broker — base, movimenti di
 * cassa, saldo di chiusura — non una somma di deal. Qui non si ricalcola
 * niente: ogni numero è quello che il backend ha scritto, e accanto a ogni
 * importo c'è la valuta in cui è, che sono due (quella del conto broker e
 * quella del ciclo) e non si deducono l'una dall'altra.
 *
 * La base dichiara SEMPRE da dove viene — misurata, a mano col suo motivo,
 * ricostruita — perché un saldo letto e un numero scritto da una persona non
 * sono la stessa cosa, e fra un anno nessuno se lo ricorda.
 */

const BASE_SOURCE: Record<CyclePeriodRow["baseSource"], { label: string; title: string }> = {
  MEASURED: { label: "misurata", title: "Saldo del conto broker letto dal gateway, a posizioni chiuse" },
  MANUAL: { label: "a mano", title: "Dichiarata da una persona: il motivo è accanto" },
  RECONSTRUCTED: {
    label: "ricostruita",
    title: "Ciclo nato prima dei periodi: la base viene dal punto zero del registro o dal saldo di chiusura del tratto precedente. Nessuno l'ha letta in quell'istante.",
  },
};

const CLOSE_REASON: Record<NonNullable<CyclePeriodRow["closeReason"]>, string> = {
  PHASE_PASSED: LABELS.sessionState.COMPLETED,
  PAYOUT: "Payout",
  BURNT: LABELS.sessionState.FAILED,
  BROKER_REPLACED: "Broker sostituito",
  BROKER_REMOVED: "Broker rimosso",
  PROP_RESET: "Reset del prop",
};

/** Perché un movimento è stato registrato e non applicato a nessuna base. */
const SKIP_REASON: Record<string, string> = {
  NO_PERIOD: "nessun periodo lo conteneva",
  BEFORE_BASE: "avvenuto prima della lettura della base: è già dentro quel saldo",
  AMOUNT_UNREADABLE: "il gateway non ha dato un importo leggibile",
  CYCLE_CLOSED: "il ciclo è chiuso",
};

export function PeriodsSection({ cycleId, canWrite }: { cycleId: string; canWrite: boolean }) {
  const q = useCyclePeriods(cycleId);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Il periodo aperto in cima, poi i chiusi dal più recente. L'ordine del
  // backend (per seq crescente) non cambia: qui si dispone soltanto.
  const rows = useMemo(() => {
    const all = q.data?.periods ?? [];
    return [...all.filter((p) => p.closedAt === null).sort((a, b) => b.seq - a.seq), ...all.filter((p) => p.closedAt !== null).sort((a, b) => b.seq - a.seq)];
  }, [q.data]);
  const editing = rows.find((p) => p.id === editingId) ?? null;

  const columns: DataTableColumn<CyclePeriodRow>[] = [
    {
      key: "phase",
      header: "Fase",
      cell: (p) => (
        <span className="whitespace-nowrap font-medium text-ink">
          {PHASE_LABEL(p.phase)} <span className="text-xs font-normal text-faint">n. {p.seq}</span>
          {p.resetsAccounting && (
            <span
              className="ml-2 rounded border border-line px-1.5 py-0.5 text-xs font-normal text-muted"
              title="Nato da un payout: da qui «da recuperare» guarda solo questo tratto"
            >
              tratto nuovo
            </span>
          )}
        </span>
      ),
    },
    {
      key: "broker",
      header: "Conto broker",
      // Il conto di QUESTO tratto, non quello attuale della Fase: il broker può
      // essere cambiato fra un periodo e l'altro.
      cell: (p) =>
        p.brokerAccount ? (
          <span className="machine-id whitespace-nowrap" title={p.brokerAccount.label}>
            {p.brokerAccount.mt5Login}
          </span>
        ) : (
          <Dash title="Conto non trovato" />
        ),
    },
    { key: "opened", header: "Apertura", cell: (p) => <span className="whitespace-nowrap">{date(p.openedAt)}</span> },
    {
      key: "base",
      header: "Base",
      align: "right",
      cell: (p) => (
        <span className="whitespace-nowrap">
          {money(p.baseAmount, p.currency)}{" "}
          <span className="text-xs text-faint" title={BASE_SOURCE[p.baseSource].title}>
            {BASE_SOURCE[p.baseSource].label}
          </span>
          {canWrite && (
            <button
              type="button"
              onClick={() => setEditingId(p.id)}
              aria-label={`Correggi la base del periodo n. ${p.seq}`}
              title="Correggi la base"
              className="ml-1 align-middle text-muted hover:text-ink"
            >
              <Pencil className="inline h-3.5 w-3.5" />
            </button>
          )}
        </span>
      ),
    },
    {
      key: "closed",
      header: "Chiusura",
      cell: (p) => (p.closedAt ? <span className="whitespace-nowrap">{date(p.closedAt)}</span> : <span className="text-muted">aperto</span>),
    },
    {
      key: "closeAmount",
      header: "Saldo di chiusura",
      align: "right",
      cell: (p) =>
        p.closedAt === null ? (
          <Dash title="Periodo aperto" />
        ) : (
          <span className="whitespace-nowrap">
            {money(p.closeAmount, p.currency)}
            {p.closeSource === "MANUAL" && (
              <span
                className="ml-1 text-xs text-faint"
                title={p.closeNote ? `Dichiarato da una persona, non letto dal gateway. Motivo: ${p.closeNote}` : "Dichiarato da una persona, non letto dal gateway"}
              >
                dichiarato
              </span>
            )}
          </span>
        ),
    },
    {
      key: "pnl",
      header: "P&L",
      align: "right",
      cell: (p) =>
        p.netNative === null ? (
          <Dash title="Periodo aperto" />
        ) : (
          <span className={`whitespace-nowrap ${signClass(Number(p.netNative))}`}>{money(p.netNative, p.currency)}</span>
        ),
    },
    {
      key: "reason",
      header: "Motivo di chiusura",
      cell: (p) => (p.closedAt === null ? <Dash title="Periodo aperto" /> : p.closeReason ? CLOSE_REASON[p.closeReason] : <Dash />),
    },
  ];

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-medium text-ink">Conto broker per fase</h2>
        {canWrite && q.data && <CashScanButton data={q.data} />}
      </div>

      {q.isLoading ? (
        <p className="card px-4 py-4 text-sm text-muted">Caricamento…</p>
      ) : q.error ? (
        <ErrorNote>{errorMessage(q.error)}</ErrorNote>
      ) : !q.data ? null : (
        <div className="space-y-3">
          {q.data.source === "LEGACY" && <LegacyNote data={q.data} />}
          <div className="card">
            <DataTable
              bare
              columns={columns}
              rows={rows}
              rowKey={(p) => p.id}
              expandedContent={(p) => <PeriodDetail p={p} />}
              empty={{
                title: "Nessun periodo",
                hint: "La contabilità per tratti nasce con la prima Fase aperta dopo il 22/09.",
              }}
            />
          </div>
          {editing && <BaseEditor key={editing.id} cycleId={cycleId} p={editing} onDone={() => setEditingId(null)} />}
          {q.data.unappliedCashMoves.length > 0 && <UnappliedMoves rows={q.data.unappliedCashMoves} />}
        </div>
      )}
    </section>
  );
}

/**
 * I numeri di questo ciclo non vengono dai periodi: mancano del tutto, o ne
 * manca qualcuno. Si dice, e basta — non è un errore da correggere qui: è uno
 * script (`backfill-periods`) o un ciclo nato prima.
 */
function LegacyNote({ data }: { data: CyclePeriods }) {
  return (
    <p className="rounded border border-line bg-raised/50 px-4 py-3 text-sm text-muted">
      Numeri calcolati dalle operazioni: a questo ciclo{" "}
      {data.periodsMissing ? "mancano i periodi" : "manca il periodo di qualche Fase già contabilizzata"}. Il netto viene
      dalla somma dei deal, non dalla differenza dei saldi del conto broker.
    </p>
  );
}

/**
 * Il resto del periodo, a riga aperta: date, tasso, motivi, movimenti di cassa.
 * Niente sparisce rispetto a prima, si è solo spostato qui sotto.
 */
function PeriodDetail({ p }: { p: CyclePeriodRow }) {
  return (
    <div className="space-y-2 text-sm">
      <p className="text-xs text-faint">
        Aperto il {dateTime(p.openedAt)} · base letta il {dateTime(p.baseAt)}
        {p.baseEditedAt ? ` · corretta il ${dateTime(p.baseEditedAt)}` : ""}
        {p.closedAt ? ` · chiuso il ${dateTime(p.closedAt)}` : ""}
        {p.fxRateAtClose ? ` · tasso ${p.currency}/${p.cycleCurrency} ${Number(p.fxRateAtClose)}${p.fxSource ? ` (${p.fxSource})` : ""}` : ""}
        {p.profitTarget !== null ? ` · obiettivo del tratto ${money(p.profitTarget, p.cycleCurrency)}` : ""}
      </p>
      <p className="text-muted">
        Movimenti di cassa:{" "}
        {Number(p.cashAdjustments) === 0 ? money(0, p.currency) : <span className={signClass(Number(p.cashAdjustments))}>{money(p.cashAdjustments, p.currency)}</span>}
        {p.cashMoves.length > 0 && <span className="text-xs text-faint"> ({p.cashMoves.length})</span>}
        {" · "}Netto {p.cycleCurrency}:{" "}
        {p.netCycle === null ? (
          <Dash title={p.closedAt === null ? "Periodo aperto" : `Tasso ${p.currency}/${p.cycleCurrency} mancante alla chiusura`} />
        ) : (
          <span className={signClass(Number(p.netCycle))}>{money(p.netCycle, p.cycleCurrency)}</span>
        )}
      </p>
      {p.baseNote && <p className="text-muted">Motivo della base: {p.baseNote}</p>}
      {p.closeNote && <p className="text-muted">Motivo del saldo di chiusura (dichiarato): {p.closeNote}</p>}

      {/*
        * Lo scarto contro il consuntivo dai deal, come DATO: nessun avviso,
        * nessun colore, nessuna etichetta. Le due misure non coincidono mai
        * al centesimo — swap, commissioni, righe che il gateway non ha
        * restituito — e chi legge il numero decide se gli interessa.
        */}
      {p.dealsDelta !== null && <p className="text-muted">Scarto contro il consuntivo dai deal: {money(p.dealsDelta, p.cycleCurrency)}</p>}

      {p.cashMoves.length > 0 && (
        <div>
          <p className="text-xs text-faint">Movimenti di cassa del periodo</p>
          <ul className="mt-1 space-y-0.5">
            {p.cashMoves.map((m) => (
              <li key={m.id} className="text-muted">
                <MoveLine m={m} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function MoveLine({ m }: { m: CashMove }) {
  return (
    <>
      {dateTime(m.occurredAt)} · {m.kind} ·{" "}
      {m.amount === null ? (
        <span className="text-faint" title="Il gateway non ha dato un importo leggibile: la riga resta, la base non si muove">
          importo non letto
        </span>
      ) : (
        <span className={signClass(Number(m.amount))}>{money(m.amount, m.currency)}</span>
      )}{" "}
      <span className="machine-id">{m.ticket}</span>
      {m.skipReason && <span className="text-faint"> · non applicato: {SKIP_REASON[m.skipReason] ?? m.skipReason}</span>}
    </>
  );
}

/**
 * I movimenti registrati che non hanno spostato nessuna base.
 *
 * Stanno fuori dall'elenco dei periodi proprio perché non ne hanno uno: se non
 * comparissero qui, un versamento che non ha mosso niente sarebbe invisibile
 * esattamente dove lo si va a cercare.
 */
function UnappliedMoves({ rows }: { rows: CashMove[] }) {
  return (
    <div className="rounded border border-warn/40 bg-warn/5 px-4 py-3 text-sm">
      <p className="text-ink">Movimenti non applicati</p>
      <ul className="mt-1 space-y-0.5">
        {rows.map((m) => (
          <li key={m.id} className="text-muted">
            <MoveLine m={m} />
          </li>
        ))}
      </ul>
      <p className="mt-1 text-xs text-faint">
        Registrati, e fuori da ogni tratto: non hanno spostato nessuna base e non sono entrati in nessun netto.
      </p>
    </div>
  );
}

/** La correzione della base: importo e MOTIVO, tutt'e due obbligatori. */
function BaseEditor({ cycleId, p, onDone }: { cycleId: string; p: CyclePeriodRow; onDone: () => void }) {
  const refresh = useRefreshDirectory();
  const [amount, setAmount] = useState(String(Number(p.baseAmount)));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const negativo = amount.trim().startsWith("-");
    const v = decimalInput(amount.replace(/^-/, ""));
    if (v === null) return setError("Scrivi la base come numero, per esempio 10250.40");
    if (reason.trim().length < 3) return setError("Scrivi il motivo della correzione");
    setBusy(true);
    setError(null);
    try {
      await api.cycles.setPeriodBase(cycleId, p.id, { baseAmount: negativo ? `-${v}` : v, reason: reason.trim() });
      await refresh();
      onDone();
    } catch (e) {
      // Il messaggio è quello del backend: CYCLE_CLOSED dice perché non si
      // corregge più, e non è la stessa cosa di un campo scritto male.
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl space-y-2 rounded border border-line bg-raised/50 p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={`Base del periodo n. ${p.seq} (${p.currency})`} hint="Resta una base DICHIARATA: correggere un saldo letto non lo rende più letto.">
          {(id) => <input id={id} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass} />}
        </Field>
        <Field label="Motivo" hint="Obbligatorio: è quello che fra un anno spiega il netto di questo tratto.">
          {(id) => <input id={id} value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass} />}
        </Field>
      </div>
      <ErrorNote>{error}</ErrorNote>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Annulla
        </Button>
        <Button size="sm" variant="primary" disabled={busy} onClick={() => void save()}>
          {busy ? "Salvataggio…" : "Salva la base"}
        </Button>
      </div>
    </div>
  );
}

/**
 * «Rileva movimenti»: chiede al gateway i movimenti di cassa adesso.
 *
 * Passa dalla Fase del periodo aperto — se non ce n'è uno, dall'ultima Fase che
 * ha avuto un periodo — perché la rotta sta sulla Fase e la finestra parte
 * dall'apertura del tratto.
 */
function CashScanButton({ data }: { data: CyclePeriods }) {
  const refresh = useRefreshDirectory();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [esito, setEsito] = useState<CashScanOutcome | null>(null);

  const sessionId = (data.periods.find((p) => p.closedAt === null) ?? [...data.periods].reverse()[0])?.sessionId ?? null;

  if (!sessionId) {
    return (
      <span className="text-xs text-faint" title="I movimenti si rilevano dalla Fase del tratto, e qui non ce n'è una">
        Nessuna Fase da cui rilevare i movimenti
      </span>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {esito && !error && (
        <span className="text-xs text-muted">
          {esito.read === 0
            ? "Nessun movimento nella finestra."
            : `${esito.read} letti, ${esito.recorded} nuovi, ${esito.applied.length} applicati${esito.skipped.length ? `, ${esito.skipped.length} non applicati` : ""}.`}
        </span>
      )}
      {error && <span className="text-xs text-loss">{error}</span>}
      <Button
        size="sm"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          setEsito(null);
          try {
            setEsito(await api.sessions.cashScan(sessionId));
            await refresh();
          } catch (e) {
            setError(errorMessage(e));
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "Lettura…" : "Rileva movimenti"}
      </Button>
    </div>
  );
}
